<template>
  <div class="music-download">
    <div class="music-download__card">
      <h1 class="music-download__title">楽曲ダウンロード</h1>

      <!-- パスワード入力画面 -->
      <div v-if="!authenticated" class="music-download__form">
        <p class="music-download__description">
          パスワードを入力してダウンロードしてください。
        </p>
        <div class="music-download__input-wrapper">
          <input
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            placeholder="パスワードを入力"
            class="music-download__input"
            @keyup.enter="verifyPassword"
          />
          <button
            class="music-download__toggle-password"
            @click="showPassword = !showPassword"
            type="button"
          >
            {{ showPassword ? '🙈' : '👁' }}
          </button>
        </div>
        <p v-if="errorMessage" class="music-download__error">{{ errorMessage }}</p>
        <button
          @click="verifyPassword"
          class="music-download__button"
          :disabled="!password || verifying"
        >
          {{ verifying ? '確認中...' : '認証してダウンロード' }}
        </button>
      </div>

      <!-- ダウンロード画面 -->
      <div v-else class="music-download__success">
        <div class="music-download__check-icon">✓</div>
        <p class="music-download__message">認証が完了しました</p>
        <p class="music-download__filename">{{ displayFilename }}</p>
        <button @click="startDownload" class="music-download__button music-download__button--download">
          ダウンロード
        </button>
        <p v-if="downloadStarted" class="music-download__done">
          ダウンロードを開始しました。
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useRoute } from "vue-router";
import { getStorage, ref as storageRef, getDownloadURL } from "firebase/storage";

const route = useRoute();

// URLクエリパラメータから取得: /music-download?id=xxx
const fileId = route.query.id;

const password = ref("");
const showPassword = ref(false);
const authenticated = ref(false);
const verifying = ref(false);
const errorMessage = ref("");
const downloadUrl = ref(null);
const downloadStarted = ref(false);

// パスワードとファイルの対応マップ
// 新しい楽曲を配布する場合はここにエントリを追加してください
// key: ファイルID (URLのid=で指定), value: { password, storagePath, filename }
const fileConfig = {
  fellinblue: {
    password: "fellinblue2026",
    storagePath: "music/fellinblue_mix01.wav",
    filename: "fellinblue_mix01.wav",
  },
};

const currentConfig = computed(() => {
  if (!fileId) return null;
  return fileConfig[fileId] || null;
});

const displayFilename = computed(() => {
  return currentConfig.value?.filename || `${fileId}.wav`;
});

const verifyPassword = async () => {
  if (!password.value) return;

  errorMessage.value = "";
  verifying.value = true;

  // ファイルIDが未設定の場合
  if (!fileId) {
    errorMessage.value = "無効なダウンロードURLです。";
    verifying.value = false;
    return;
  }

  // ファイル設定が見つからない場合
  if (!currentConfig.value) {
    errorMessage.value = "無効なダウンロードURLです。";
    verifying.value = false;
    return;
  }

  // パスワード照合
  if (password.value !== currentConfig.value.password) {
    errorMessage.value = "パスワードが正しくありません。";
    verifying.value = false;
    return;
  }

  // Firebase Storageからダウンロード用URLを取得
  try {
    const storage = getStorage();
    const fileRef = storageRef(
      storage,
      `gs://rf-prd-sukisuki-ls.appspot.com/${currentConfig.value.storagePath}`
    );
    downloadUrl.value = await getDownloadURL(fileRef);
    authenticated.value = true;
  } catch (err) {
    console.error("Error fetching file:", err);
    // フォールバック: 直接URLを試す
    try {
      const directUrl = `https://storage.googleapis.com/rf-prd-sukisuki-ls.appspot.com/${currentConfig.value.storagePath}`;
      const response = await fetch(directUrl, { method: "HEAD" });
      if (response.ok) {
        downloadUrl.value = directUrl;
        authenticated.value = true;
      } else {
        errorMessage.value = "ファイルが見つかりません。";
      }
    } catch {
      errorMessage.value = "ファイルの取得に失敗しました。";
    }
  } finally {
    verifying.value = false;
  }
};

const startDownload = () => {
  if (!downloadUrl.value) return;

  const link = document.createElement("a");
  link.href = downloadUrl.value;
  link.download = displayFilename.value;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  downloadStarted.value = true;
};
</script>

<style scoped>
.music-download {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  padding: 20px;
}

.music-download__card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 40px 32px;
  max-width: 440px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  text-align: center;
}

.music-download__title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 24px;
}

.music-download__description {
  color: #555;
  font-size: 0.95rem;
  margin-bottom: 24px;
  line-height: 1.6;
}

.music-download__input-wrapper {
  position: relative;
  margin-bottom: 8px;
}

.music-download__input {
  width: 100%;
  padding: 14px 48px 14px 16px;
  border: 2px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.music-download__input:focus {
  border-color: #0f3460;
}

.music-download__toggle-password {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.2rem;
  padding: 4px;
}

.music-download__error {
  color: #e74c3c;
  font-size: 0.85rem;
  margin: 8px 0 16px;
  text-align: left;
}

.music-download__button {
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, #0f3460, #1a1a2e);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.1s;
  margin-top: 16px;
}

.music-download__button:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
}

.music-download__button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.music-download__button--download {
  background: linear-gradient(135deg, #27ae60, #2ecc71);
}

.music-download__success {
  padding: 16px 0;
}

.music-download__check-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #27ae60;
  color: white;
  font-size: 2rem;
  line-height: 64px;
  margin: 0 auto 16px;
}

.music-download__message {
  color: #27ae60;
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 8px;
}

.music-download__filename {
  color: #777;
  font-size: 0.9rem;
  margin-bottom: 24px;
}

.music-download__done {
  color: #555;
  font-size: 0.9rem;
  margin-top: 16px;
}

@media (max-width: 480px) {
  .music-download__card {
    padding: 32px 20px;
  }

  .music-download__title {
    font-size: 1.3rem;
  }
}
</style>
